import { useState, useMemo, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons'
import { v4 as uuidv4 } from 'uuid'
import { createForm, FormField } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import LegalModal from '../../components/LegalModal'
import { TERMS_OF_USE, PRIVACY_POLICY } from '../../legal'
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate'

// Password validation rules
const validatePassword = (password: string) => ({
  minLength: password.length >= 8,
  hasUppercase: /[A-Z]/.test(password),
  hasLowercase: /[a-z]/.test(password),
  hasNumber: /[0-9]/.test(password),
  hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
})

const isPasswordValid = (password: string) => {
  const rules = validatePassword(password)
  return Object.values(rules).every(Boolean)
}

export default function CreateForm() {
  const { t, i18n } = useTranslation()
  const navigate = useLocalizedNavigate()
  const { user } = useAuth()
  const isLoggedIn = !!user
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fields, setFields] = useState<FormField[]>([
    { id: uuidv4(), name: '', type: 'text', required: true },
  ])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [createdFormId, setCreatedFormId] = useState<string | null>(null)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [legalModalOpen, setLegalModalOpen] = useState<'terms' | 'privacy' | null>(null)

  const isRtl = i18n.language === 'he'

  useEffect(() => { document.title = `${t('createForm.title')} | ${t('common.appName')}` }, [t])

  const passwordRules = useMemo(() => validatePassword(password), [password])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif']
      if (!validTypes.includes(selectedFile.type)) {
        setError(t('createForm.validation.invalidFileType'))
        return
      }
      setFile(selectedFile)
      setError('')
    }
  }

  const addField = () => {
    setFields([...fields, { id: uuidv4(), name: '', type: 'text', required: false }])
  }

  const removeField = (id: string) => {
    if (fields.length > 1) {
      setFields(fields.filter((f) => f.id !== id))
    }
  }

  const updateField = (id: string, updates: Partial<FormField>) => {
    setFields(fields.map((f) => (f.id === id ? { ...f, ...updates } : f)))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!file) {
      setError(t('createForm.validation.fileRequired'))
      return
    }
    if (!title.trim()) {
      setError(t('createForm.validation.titleRequired'))
      return
    }
    if (!isLoggedIn) {
      if (!password) {
        setError(t('createForm.validation.passwordRequired'))
        return
      }
      if (!isPasswordValid(password)) {
        setError(t('createForm.validation.passwordWeak'))
        return
      }
      if (password !== confirmPassword) {
        setError(t('createForm.validation.passwordMismatch'))
        return
      }
    }

    const emptyFields = fields.filter((f) => !f.name.trim())
    if (emptyFields.length > 0) {
      setError(t('createForm.validation.fillAllFields'))
      return
    }
    if (!acceptedTerms) {
      setError(t('legal.mustAcceptTerms'))
      return
    }

    setLoading(true)
    setError('')

    try {
      const result = await createForm(file, title, isLoggedIn ? null : password, fields, acceptedTerms)
      if (!isLoggedIn) {
        sessionStorage.setItem(`form_${result.id}_password`, password)
      }
      setCreatedFormId(result.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.createFormError'))
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  // Success screen after form creation
  if (createdFormId) {
    return (
      <div className="container">
        <div style={{ maxWidth: '600px', margin: '3rem auto' }}>
          <div className="card text-center" role="status">
            <FontAwesomeIcon
              icon="check"
              style={{ fontSize: '3rem', color: 'var(--success)', marginBottom: '1rem' }}
            />
            <h1>{t('createForm.success.title')}</h1>

            {!isLoggedIn && (
              <div style={{ background: '#fefce8', border: '1px solid #facc15', borderRadius: 'var(--radius)', padding: '1rem', margin: '1.5rem 0', textAlign: isRtl ? 'right' : 'left' }}>
                <p style={{ fontWeight: 600, color: '#854d0e', marginBottom: '0.5rem' }}>
                  {t('createForm.success.saveFormId')}
                </p>
                <div className="flex items-center gap-2" style={{ background: 'white', padding: '0.75rem', borderRadius: 'var(--radius)' }}>
                  <code style={{ flex: 1, fontSize: '0.9rem', wordBreak: 'break-all' }} dir="ltr">{createdFormId}</code>
                  <button className="btn btn-outline" onClick={() => copyToClipboard(createdFormId)} aria-label={t('common.copy')}>
                    <FontAwesomeIcon icon="copy" />
                  </button>
                </div>
                <p className="text-sm mt-2" style={{ color: '#854d0e' }}>
                  {t('createForm.success.formIdNote')}
                </p>
              </div>
            )}

            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 'var(--radius)', padding: '1rem', margin: '1rem 0', textAlign: isRtl ? 'right' : 'left' }}>
              <p className="text-sm" style={{ color: '#15803d' }}>
                {t('createForm.success.activationNote')}
              </p>
            </div>

            <button
              className="btn btn-primary btn-lg"
              onClick={() => navigate(`/dashboard/${createdFormId}`)}
            >
              {t('createForm.success.continueToManage')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container">
      <div style={{ maxWidth: '700px', margin: '0 auto' }}>
        <div className="page-header">
          <button className="btn btn-outline btn-icon" onClick={() => navigate('/')} aria-label={t('common.back')}>
            <FontAwesomeIcon icon="arrow-left" />
          </button>
          <h1>{t('createForm.title')}</h1>
        </div>

        {!isLoggedIn && (
          <div
            className="card mb-4"
            style={{
              background: 'linear-gradient(135deg, #f0fdf4, #ecfdf5)',
              border: '1px solid #bbf7d0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              animation: 'auth-promo-glow 2s ease-in-out infinite',
            }}
          >
            <style>{`
              @keyframes auth-promo-glow {
                0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.15); }
                50% { box-shadow: 0 0 12px 2px rgba(34, 197, 94, 0.2); }
              }
              @keyframes auth-promo-bounce {
                0%, 100% { transform: translateX(0); }
                50% { transform: translateX(4px); }
              }
              [dir="rtl"] .auth-promo-arrow { transform: scaleX(-1); }
              [dir="rtl"] @keyframes auth-promo-bounce {
                0%, 100% { transform: translateX(0) scaleX(-1); }
                50% { transform: translateX(-4px) scaleX(-1); }
              }
            `}</style>
            <div style={{ flex: 1 }}>
              <p style={{ color: '#15803d', fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.125rem' }}>
                {t('home.authPromoBanner')}
              </p>
            </div>
            <Link
              to={`/${i18n.language}/login?redirect=/create`}
              className="btn btn-primary"
              style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.375rem' }}
            >
              {t('home.authPromoAction')}
              <span className="auth-promo-arrow" style={{ animation: 'auth-promo-bounce 1s ease-in-out infinite' }} aria-hidden="true">→</span>
            </Link>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="card mb-4">
            <h3>{t('createForm.uploadDocument')}</h3>
            <label className={`file-upload ${file ? 'has-file' : ''}`}>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif"
                onChange={handleFileChange}
                aria-label={t('createForm.uploadDocument')}
              />
              <div className="upload-icon">
                <FontAwesomeIcon
                  icon={file ? 'check' : 'upload'}
                  style={{ fontSize: '1.5rem', color: file ? 'var(--success)' : 'var(--primary)' }}
                />
              </div>
              <div className="upload-text">
                {file ? (
                  <>
                    <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%', display: 'block' }}>{file.name}</strong>
                    <p>{t('createForm.clickToReplace')}</p>
                  </>
                ) : (
                  <>
                    <strong>{t('createForm.clickToUpload')}</strong>
                    <p>{t('createForm.fileTypes')}</p>
                  </>
                )}
              </div>
            </label>
          </div>

          <div className="card mb-4">
            <h3>{t('createForm.formDetails')}</h3>

            <div className="form-group">
              <label className="form-label" htmlFor="create-title">{t('createForm.formTitle')}</label>
              <input
                id="create-title"
                type="text"
                className="form-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('createForm.formTitlePlaceholder')}
              />
            </div>

            {!isLoggedIn && (
              <>
                <div className="form-group">
                  <label className="form-label" htmlFor="create-password">{t('createForm.password')}</label>
                  <input
                    id="create-password"
                    type="password"
                    className="form-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('createForm.passwordPlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="create-confirm-password">{t('createForm.confirmPassword')}</label>
                  <input
                    id="create-confirm-password"
                    type="password"
                    className="form-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('createForm.confirmPasswordPlaceholder')}
                  />
                </div>

                {password && (
                  <div className="password-requirements">
                    <ul>
                      <li className={passwordRules.minLength ? 'valid' : 'invalid'}>
                        <FontAwesomeIcon icon={passwordRules.minLength ? faCheck : faXmark} />
                        {t('createForm.passwordRules.minLength')}
                      </li>
                      <li className={passwordRules.hasUppercase ? 'valid' : 'invalid'}>
                        <FontAwesomeIcon icon={passwordRules.hasUppercase ? faCheck : faXmark} />
                        {t('createForm.passwordRules.uppercase')}
                      </li>
                      <li className={passwordRules.hasLowercase ? 'valid' : 'invalid'}>
                        <FontAwesomeIcon icon={passwordRules.hasLowercase ? faCheck : faXmark} />
                        {t('createForm.passwordRules.lowercase')}
                      </li>
                      <li className={passwordRules.hasNumber ? 'valid' : 'invalid'}>
                        <FontAwesomeIcon icon={passwordRules.hasNumber ? faCheck : faXmark} />
                        {t('createForm.passwordRules.number')}
                      </li>
                      <li className={passwordRules.hasSpecial ? 'valid' : 'invalid'}>
                        <FontAwesomeIcon icon={passwordRules.hasSpecial ? faCheck : faXmark} />
                        {t('createForm.passwordRules.special')}
                      </li>
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="card mb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 style={{ marginBottom: 0 }}>{t('createForm.fieldsToFill')}</h3>
              <button type="button" className="btn btn-outline" onClick={addField}>
                <FontAwesomeIcon icon="plus" />
                {t('createForm.addField')}
              </button>
            </div>

            {fields.map((field, index) => (
              <div
                key={field.id}
                className="field-card"
              >
                <div className="field-card-header">
                  <span className="field-card-number">{index + 1}</span>
                  <input
                    id={`field-name-${field.id}`}
                    type="text"
                    className="form-input"
                    value={field.name}
                    onChange={(e) => updateField(field.id, { name: e.target.value })}
                    placeholder={t('createForm.fieldName')}
                    aria-label={`${t('createForm.fieldName')} ${index + 1}`}
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline btn-icon"
                    onClick={() => removeField(field.id)}
                    disabled={fields.length === 1}
                    style={{ flexShrink: 0 }}
                    aria-label={t('common.delete')}
                  >
                    <FontAwesomeIcon icon="trash" />
                  </button>
                </div>
                <div className="field-card-options">
                  <select
                    className="form-select"
                    value={field.type}
                    onChange={(e) => updateField(field.id, { type: e.target.value as FormField['type'] })}
                  >
                    <option value="text">{t('createForm.fieldTypes.text')}</option>
                    <option value="number">{t('createForm.fieldTypes.number')}</option>
                    <option value="email">{t('createForm.fieldTypes.email')}</option>
                    <option value="tel">{t('createForm.fieldTypes.tel')}</option>
                  </select>
                  <label className="field-card-required">
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) => updateField(field.id, { required: e.target.checked })}
                    />
                    {t('common.required')}
                  </label>
                </div>
              </div>
            ))}
          </div>

          <div className="card mb-4">
            <div className="legal-acceptance">
              <input
                type="checkbox"
                id="accept-terms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
              />
              <label htmlFor="accept-terms">
                {t('legal.acceptTerms')}{' '}
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => setLegalModalOpen('terms')}
                >
                  {t('legal.termsOfUse')}
                </button>{' '}
                {t('legal.and')}{' '}
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => setLegalModalOpen('privacy')}
                >
                  {t('legal.privacyPolicy')}
                </button>
              </label>
            </div>
          </div>

          {error && (
            <div className="card mb-4" style={{ background: '#fef2f2', borderColor: 'var(--danger)' }} role="alert">
              <p className="text-danger">{error}</p>
            </div>
          )}

          <button type="submit" className="btn btn-primary btn-lg w-full" disabled={loading || !acceptedTerms}>
            {loading ? (
              <>
                <FontAwesomeIcon icon="spinner" className="spinner" />
                {t('createForm.creating')}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon="check" />
                {t('createForm.createFormBtn')}
              </>
            )}
          </button>
        </form>
      </div>

      <LegalModal
        isOpen={legalModalOpen === 'terms'}
        onClose={() => setLegalModalOpen(null)}
        type="terms"
        content={(TERMS_OF_USE as any)[i18n.language] || TERMS_OF_USE.en}
      />
      <LegalModal
        isOpen={legalModalOpen === 'privacy'}
        onClose={() => setLegalModalOpen(null)}
        type="privacy"
        content={(PRIVACY_POLICY as any)[i18n.language] || PRIVACY_POLICY.en}
      />
    </div>
  )
}
